import { FormattedMessage } from 'react-intl';
import { usePreviewBulk } from 'Modules/BulkBooking/Hooks/usePreviewBulk';
import BulkBookingResults from 'Modules/BulkBooking/Components/BulkBookingResults';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { WizardStepHeader } from '../Shared/WizardStepHeader';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { normaliseInterval } from 'Modules/BulkBooking/Recurrence';

export default function BulkTicketsReviewTab() {
	const { wizardData, setWizardData } = useBookingWizardDataStore();
	const startLocationId = wizardData.fromLocationId;
	const endLocationId = wizardData.toLocationId;

	const { bulkBookingFilters } = wizardData;

	// We can assert ! here because we check visibility in the wrapper
	const { startDate, endDate, frequency, interval, daysOfWeek, weekOrdinals, departureTime } = bulkBookingFilters!;

	const shouldFetch = !!startDate && !!endDate && daysOfWeek.length > 0 && !!startLocationId && !!endLocationId;

	const { data, isLoading } = usePreviewBulk(
		{
			startDate: startDate!,
			endDate: endDate!,
			daysOfWeek,
			frequency,
			interval: normaliseInterval(interval),
			weekOrdinals,
			departureTime: departureTime ? dayjs(departureTime).format('HH:mm') : '',
			startLocationId: startLocationId!,
			endLocationId: endLocationId!,
		},
		shouldFetch,
	);

	// The server decides both numbers: how many trips matched, and the limit it enforced. Deriving
	// them from the response rather than from `items` and the local app config keeps the wizard
	// from disagreeing with the server about whether a search is over the limit — and the response
	// carries no items at all when it is.
	const matchedTripCount = data?.matchedTripCount ?? 0;
	const isLimitExceeded = !!data && matchedTripCount > data.maxTrips;

	useEffect(() => {
		if (data?.items) {
			const validTripIds = data.items
				.filter(item => item.status === 'Scheduled' && item.tripId)
				.map(item => item.tripId!);

			const freshWizardData = useBookingWizardDataStore.getState().wizardData;

			setWizardData(
				new BookingWizardData({
					...freshWizardData,
					bulkBookingFilters: {
						...freshWizardData.bulkBookingFilters!,
						// `matchedTripCount` and `items` come from two different queries — a
						// trip-stop count and the schedule query — that are meant to agree but are
						// not held together by anything. Re-check the resolved trips against the
						// server's own limit so a divergence cannot walk past the gate and hit the
						// create endpoint's bare "Too many trips" exception.
						tripIds: validTripIds.length > data.maxTrips ? [] : validTripIds,
					},
				}),
			);
		}
	}, [data, setWizardData]);

	return (
		<div className="h-full min-h-[16rem] mb-[10rem] p-4">
			<WizardStepHeader
				iconName="fact_check"
				title={
					<FormattedMessage id="booking-wizard__bulk-tickets__review__header" defaultMessage="Review trips" />
				}
				subtitle={
					<FormattedMessage
						id="booking-wizard__bulk-tickets__review__subtitle"
						defaultMessage="Review the trips found for your bulk booking before continuing."
					/>
				}
			/>
			{isLoading && (
				<div className="flex flex-col items-center justify-center h-full">
					<LottieSpinner />
				</div>
			)}

			{isLimitExceeded && (
				<MessageBox status="error" noBottomMargin className="mb-4">
					<p className="mb-1 font-bold">
						<FormattedMessage
							id="booking-wizard__bulk-tickets__review__limit-title"
							defaultMessage="Search limit exceeded"
						/>
					</p>
					<FormattedMessage
						id="booking-wizard__bulk-tickets__review__limit-description"
						defaultMessage="Max {max} trips allowed but {count} trips found. Please reduce your search range."
						values={{ max: data?.maxTrips ?? 0, count: matchedTripCount }}
					/>
				</MessageBox>
			)}

			{!isLimitExceeded && !isLoading && (
				<BulkBookingResults departureTime={departureTime} items={data?.items || []} isLoading={isLoading} />
			)}
		</div>
	);
}
